package com.neurobs.ccabserver.administrator.controller

import com.neurobs.ccab.{ExaminerComment, GetTestDetailData, GetTestDetailMessage, SendMessageAdapter, SetCommentData, SetCommentMessage, SetTagsData, SetTagsMessage, TestRunDetail}
import com.neurobs.js.dom.{AnchorLink, CollapsibleBox, DivTextBox, ElementFactory, Grid, GridCell, HideableRowGrid, InputCheckBox}
import com.neurobs.js.util.StringUtilities.timeString
import com.neurobs.js.view.{Container, Gui}
import com.neurobs.util.SecondsTimestamp
import com.neurobs.ccab.MessageData._
import com.neurobs.ccab.db.ExaminerTagData
import scalatags.JsDom.all._
import io.circe.generic.auto._
import org.joda.time.chrono.JulianChronology
import org.scalajs.dom.{Event, MouseEvent}
import org.scalajs.dom.html.{Div, Element, Table}

import scala.collection.mutable
import scala.collection.mutable.ArrayBuffer
import scala.scalajs.js
import scala.scalajs.js.annotation.{JSGlobal, JSImport}

class ExaminerTagList(all: Seq[ExaminerTagData])
{
	val testRunTags: Map[String, Seq[ExaminerTagData]] = all.filter(_.displayNameTestRun.isDefined).groupBy(_.groupName)
	val sessionTags: Map[String, Seq[ExaminerTagData]] = all.filter(_.displayNameSession.isDefined).groupBy(_.groupName)

	def makeSelectionBox(testRun: Boolean, onSelect: String => Unit): Table = {
		val innerTdStyle = "text-align: center; padding: 2px;"
		val tdStyle = "vertical-align: top; padding: 5px"
		val grouped = if (testRun) testRunTags else sessionTags
		val toName = (t: ExaminerTagData) => if (testRun) t.displayNameTestRun.get else t.displayNameSession.get

		def makeGroup(title: String, tags: Seq[ExaminerTagData]): Element = {
			val tab = table().render
			if (title.nonEmpty) tab.appendChild(tr(td(style := innerTdStyle, b(title))).render)
			for (tag <- tags) {
				val link = ElementFactory.makeLink(toName(tag))
				link.onclick = {_ => onSelect(tag.identifier)}
				tab.appendChild(tr(td(style := innerTdStyle, link)).render)
			}
			tab
		}

		val row = tr(`class` := "efactory-table-row").render
		grouped.get("") foreach { topLevel =>
			row.appendChild(td(style := tdStyle, makeGroup(title = "", tags = topLevel)).render)
		}
		for (group <- grouped) {
			if (group._1.nonEmpty) {
				row.appendChild(td(style := tdStyle, makeGroup(title = group._1, tags = group._2)).render)
			}
		}
		table(`class` := "efactory-table", row).render
	}

	def getDisplayName(identifier: String, session: Boolean): String = {
		all.find(_.identifier == identifier).flatMap(t => if (session) t.displayNameSession else t.displayNameTestRun).getOrElse(identifier)
	}
}

object CommentState extends Enumeration {
	val Adding = Value
	val Display = Value
	val Editing = Value
	val Saving = Value
}

trait CommentHandlerDelegate {
	def save(current: ExaminerComment, newText: String): Unit
	def delete(current: ExaminerComment): Unit
	def delete(tag: String, comment: Option[ExaminerComment]): Unit
	def add(comment: ExaminerComment): Unit
}

class CommentHandler(session: Boolean, private var comment: Option[ExaminerComment], val tag: Option[String], delegate: CommentHandlerDelegate, readOnly: Boolean, tagList: ExaminerTagList, useTagComments: Boolean) {
	def commentId: Option[Int] = comment.map(_.id)

	private val timeBox = span(comment.map(c => timeString(c.time)).getOrElse(""): String).render

	private val tagCheck = InputCheckBox(ElementFactory.makeCheckbox(checked = true))
	tagCheck.input.onclick = (e: MouseEvent) => {
		if (!readOnly) {
			delegate.delete(tag = tag.getOrElse(""), comment = comment)
			e.preventDefault()
		}
		false
	}
	tagCheck.setReadOnly(readOnly)
	private val tagBox = span(tagCheck.input, (" " + tag.map(tagList.getDisplayName(_, session)).getOrElse("")): String).render

	private val leftColumn = if (tag.isDefined) tagBox else timeBox

	private val deleteLink = AnchorLink(ElementFactory.makeLink(text = "delete"))
	deleteLink.setHandler { () => delegate.delete(comment.get) }

	private val editLink = AnchorLink(ElementFactory.makeLink(text = "edit"))
	editLink.setHandler { () => setState(CommentState.Editing) }

	private val saveLink = AnchorLink(ElementFactory.makeLink(text = "save"))
	saveLink.setHandler { () =>
		val newText = textBox.value()
		if (newText == comment.get.text) setState(CommentState.Display)
		else {
			state = CommentState.Saving
			delegate.save(comment.get, newText)
		}
	}

	private val cancelLink = AnchorLink(ElementFactory.makeLink(text = "cancel"))
	cancelLink.setHandler { () => setState(CommentState.Display) }

	private val addLink = AnchorLink(ElementFactory.makeLink(text = "Add Tag Comment"))
	addLink.setHandler { () =>
		if (textBox.value().nonEmpty) {
			delegate.add(ExaminerComment(id = 0, time = SecondsTimestamp.now(), text = textBox.value(), tag = tag))
		}
	}

	private val linkTable = table(tr(td(editLink.anchor, saveLink.anchor, addLink.anchor)), tr(td(deleteLink.anchor, cancelLink.anchor))).render

	private var state = if (tag.isDefined && comment.isEmpty) CommentState.Adding else CommentState.Display

	private val textBox = new DivTextBox

	private def setState(newState: CommentState.Value): Unit = {
		if (newState == CommentState.Display) {
			textBox.setReadOnly(true)
			textBox.setValue(comment.map(_.text).getOrElse(""))
		}
		else {
			textBox.setReadOnly(false)
		}
		def displayStyle(display: Boolean): String = if (display) "inline" else "none"
		val editing = (newState != CommentState.Display)
		textBox.element.style.display = if (newState != CommentState.Adding || useTagComments) "inline-block" else "none"
		deleteLink.anchor.style.display = displayStyle(!editing)
		editLink.anchor.style.display = displayStyle(!editing)
		saveLink.anchor.style.display = displayStyle(editing && comment.isDefined)
		cancelLink.anchor.style.display = displayStyle(editing && comment.isDefined)
		addLink.anchor.style.display = displayStyle(editing && comment.isEmpty && useTagComments)

		state = newState
	}

	def reset(newComment: Option[ExaminerComment]): Unit = {
		comment = newComment
		timeBox.innerHTML = comment.map(c => timeString(c.time)).getOrElse("")
		if (!comment.isDefined) {
			if (state != CommentState.Adding) textBox.setValue("")
			setState(CommentState.Adding)
		}
		else state match {
			case CommentState.Adding => setState(CommentState.Display)
			case CommentState.Display => setState(CommentState.Display)
			case CommentState.Saving => setState(CommentState.Display)
			case CommentState.Editing =>
		}
	}

	def getRow: Seq[GridCell] = {
		import Grid._
		if (readOnly) Seq[GridCell](leftColumn, textBox.element)
		else Seq[GridCell](leftColumn, textBox.element, linkTable)
	}

	setState(state)
}

trait ExaminerCommentControllerDelegate {
	def setComment(data: SetCommentData): Unit
	def setTags(data: SetTagsData): Unit
}

class TestExaminerCommentControllerDelegate extends ExaminerCommentControllerDelegate {
	private val comments = new ArrayBuffer[ExaminerComment]()
	private var tags = Seq[String]()
	var controller: Option[ExaminerCommentController] = None

	override def setComment(data: SetCommentData): Unit = {
		if (data.add) {
			comments.append(data.comment.copy(id = comments.size))
		}
		else {
			val i = comments.indexWhere(_.id == data.comment.id)
			if (i >= 0) {
				if (data.comment.text.isEmpty) comments.remove(i)
				else comments(i) = data.comment
			}
		}
		controller.foreach(_.update(comments, tags))
	}

	override def setTags(data: SetTagsData): Unit = {
		tags = data.tags
		controller.foreach(_.update(comments, tags))
	}
}

class MessageExaminerCommentControllerDelegate(sender: SendMessageAdapter) extends ExaminerCommentControllerDelegate {
	override def setComment(data: SetCommentData): Unit = {
		sender.send(SetCommentMessage(data = data))
	}

	override def setTags(data: SetTagsData): Unit = {
		sender.send(SetTagsMessage(data = data))
	}
}

/** Handles the GUI for examiner commenting
  *
  * @param testLogIndex Index of test run in test run log, or None if comments are for the session
  * @param delegate Event handler
  * @param gui Used for confirmation dialogs
  */
class ExaminerCommentController(testLogIndex: Option[Int], delegate: ExaminerCommentControllerDelegate, tags: ExaminerTagList, useTagComments: Boolean, gui: Gui, readOnly: Boolean = false) {
	val isSession: Boolean = testLogIndex.isEmpty
	private var waitingForAdd = false

	private var handlers = Seq[CommentHandler]()

	private val tagSelect =  tags.makeSelectionBox(testRun = testLogIndex.isDefined, { t =>
		if (!currentTags.contains(t)) {
			delegate.setTags(SetTagsData(
				tags = currentTags :+ t,
				test_log_index = testLogIndex
			))
		}
	})

	private val grid = new HideableRowGrid(fullWidth = false, emGapSize = 1, alignItems = Grid.alignItemsCenter)
	private val commentGrid = new Grid(fullWidth = false, emGapSize = 1, alignItems = Grid.alignItemsCenter)

	private val newCommentBox = new DivTextBox

	private val addLink = AnchorLink(ElementFactory.makeLink(text = "Add Comment"))
	addLink.setHandler { () =>
		if (newCommentBox.value().nonEmpty) {
			delegate.setComment(SetCommentData(
				comment = ExaminerComment(id = 0, time = SecondsTimestamp.now(), text = newCommentBox.value(), tag = None),
				test_log_index = testLogIndex,
				add = true
			))
			waitingForAdd = true
		}
	}

	private val showTagsLink = AnchorLink(ElementFactory.makeLink(text = "Tags"))
	showTagsLink.setHandler { () =>
		if (showTagsLink.getText() == "Tags") {
			grid.setRowVisible(tagSelect, visible = true)
			showTagsLink.setText("Hide Tags")
		}
		else {
			grid.setRowVisible(tagSelect, visible = false)
			showTagsLink.setText("Tags")
		}
	}

	private var currentTags: Seq[String] = Seq()

	private val handlerDelegate = new CommentHandlerDelegate {
		override def save(current: ExaminerComment, newText: String): Unit = {
			delegate.setComment(SetCommentData(
				comment = current.copy(text = newText),
				test_log_index = testLogIndex,
				add = false
			))
		}
		override def delete(current: ExaminerComment): Unit = {
			gui.showConfirmationDialog(title = "Delete Comment", yesNoQuestion = "Delete this comment?") { () =>
				delegate.setComment(SetCommentData(
					comment = current.copy(text = ""),
					test_log_index = testLogIndex,
					add = false
				))
			}
		}
		override def delete(tag: String, comment: Option[ExaminerComment]): Unit = {
			val confirmation = if (comment.isEmpty) s"Delete the tag $tag?" else s"Delete the tag $tag and the associated comment?"
			gui.showConfirmationDialog(title = "Delete Tag", yesNoQuestion = confirmation) { () =>
				comment foreach { c =>
					delegate.setComment(SetCommentData(
						comment = c.copy(text = ""),
						test_log_index = testLogIndex,
						add = false
					))
				}
				delegate.setTags(SetTagsData(
					tags = currentTags.filterNot(_ == tag),
					test_log_index = testLogIndex
				))
			}
		}
		override def add(comment: ExaminerComment): Unit = {
			delegate.setComment(SetCommentData(
				comment = comment,
				test_log_index = testLogIndex,
				add = true
			))
		}
	}

	def update(comments: Seq[ExaminerComment], tags: Seq[String]): Unit = {
		currentTags = tags
		commentGrid.resize(size = if (readOnly) 0 else 1)

		val tagHandlers = tags map { t =>
			val comment = comments.find(_.tag == Some(t))
			handlers.find(_.tag == Some(t)) match {
				case None => new CommentHandler(session = isSession, comment = comment, tag = Some(t), handlerDelegate, readOnly, tagList = this.tags, useTagComments = useTagComments)
				case Some(h) => h.reset(comment); h
			}
		}

		val reverseChrono = comments.filter(_.tag.isEmpty).sortBy(-_.time.instant.toEpochMilli)
		val otherHandlers = reverseChrono map { c =>
			handlers.find(_.commentId == Some(c.id)) match {
				case None => new CommentHandler(session = isSession, comment = Some(c), tag = None, handlerDelegate, readOnly, tagList = this.tags, useTagComments = useTagComments)
				case Some(h) => h.reset(Some(c)); h
			}
		}

		handlers = tagHandlers ++ otherHandlers
		handlers.foreach(h => commentGrid.addRowCells(h.getRow))

		if (waitingForAdd) {
			val newComment = newCommentBox.value()
			if (newComment.nonEmpty && reverseChrono.exists(_.text == newComment)) {
				newCommentBox.clear()
				waitingForAdd = false
			}
		}
	}

	import Grid._
	val container: Container = {
		import com.neurobs.js.dom.DOMViewElement._

		grid.addRow(tagSelect)
		grid.setRowVisible(tagSelect, visible = false)
		if (!readOnly) {
			commentGrid.addRowCells(Seq(showTagsLink.anchor, newCommentBox.element, addLink.anchor))
		}
		grid.addRow(commentGrid.container)
		grid.container
	}
}

object ExaminerCommentController {
	val ignoreTag = "ignore"
}

case class TestRunDetailInstance(logIndex: Int, detail: TestRunDetail, commentController: ExaminerCommentController)

class TestRunDetailCache(sender: SendMessageAdapter, gui: Gui, tags: ExaminerTagList, useTagComments: Boolean) {
	private val map = mutable.Map[Int, (Option[TestRunDetail], ExaminerCommentController)]()

	def clear(): Unit = map.clear()

	def set(logIndex: Int, detail: TestRunDetail): Unit = {
		val comments = map.get(logIndex) match {
			case Some(d) => d._2
			case None =>
				new ExaminerCommentController(Some(logIndex), new MessageExaminerCommentControllerDelegate(sender), tags = tags, gui = gui, useTagComments = useTagComments)
		}
		comments.update(detail.comments.getOrElse(Seq()), detail.tags.getOrElse(Seq()))
		map += (logIndex -> (Some(detail), comments))
	}

	def get(logIndex: Int): Option[TestRunDetailInstance] = {
		val res = map.get(logIndex)
		val detail = res.flatMap(_._1)
		if (detail.isEmpty) sender.send(GetTestDetailMessage(data = GetTestDetailData(logIndex)))
		res.flatMap(p => detail.map(TestRunDetailInstance(logIndex, _, p._2)))
	}

	def invalidate(logIndex: Int): Unit = {
		map.get(logIndex) foreach { i =>
			map(logIndex) = (None, i._2)
		}
	}
}